import { startTransition, useCallback } from 'react';
import { type WithTestId } from '@pkgs/components/types/types';
import { useShopFilterRemovalMessage } from '@pkgs/components/hooks/useShopFilterRemovalMessage';
import { useLocationFilterInitialLocation } from '@pkgs/components/hooks/useLocationFilterInitialLocation';
import { useIsLipaPage } from '@pkgs/components/hooks/useIsLipaPage';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useAutoCompleteStore } from '@pkgs/components/hooks/stores/useAutoCompleteStore';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { Dialog } from '@base-ui/react/dialog';

import { LocationFilterModalTabs } from './LocationFilterModalTabs';
import { MobileAutoComplete } from '../AutoComplete/MobileAutoComplete';
import { type AutoSuggestOption } from '../AutoComplete/AutoComplete';

import styles from './LocationFilter.module.css';

export type LocationFilterProps = WithTestId<{
  className?: string;
}>;

export const MobileLocationFilter = (props: LocationFilterProps) => {
  const { featureFlags, t } = useAppContext();
  const isMobileModalOpen = useAutoCompleteStore((state) => state.isMobileModalOpen);
  const setIsMobileModalOpen = useAutoCompleteStore((state) => state.setIsMobileModalOpen);
  const setPlaceIds = useSearchStore((state) => state.setPlaceIds);
  const setPlaceName = useSearchStore((state) => state.setPlaceName);
  const searchMode = useSearchStore((state) => state.searchMode);
  const { removeShopFilterWithUndoMessage } = useShopFilterRemovalMessage();
  const isLipaPage = useIsLipaPage();

  const handleSelection = useCallback(
    (selectedLocation: AutoSuggestOption) => {
      setIsMobileModalOpen(false);
      startTransition(() => {
        if (isLipaPage) {
          removeShopFilterWithUndoMessage();
        }
        setPlaceIds([selectedLocation.id]);
        setPlaceName(selectedLocation.title);
      });
    },
    [isLipaPage, removeShopFilterWithUndoMessage, setIsMobileModalOpen, setPlaceIds, setPlaceName],
  );

  const initialLocation = useLocationFilterInitialLocation({ visibleSearchMode: searchMode });

  if (!isMobileModalOpen) {
    return <MobileAutoComplete handleSelection={handleSelection} initialLocation={initialLocation} locationFilterProps={props} />;
  }

  return (
    <Dialog.Root open onOpenChange={setIsMobileModalOpen}>
      <Dialog.Portal>
        <Dialog.Popup
          className={styles.locationFilterModal}
          data-testid="search-components_location-filter_mobile_modal"
          aria-label={t('search.searchbar.locationInput.label')}
        >
          {featureFlags.isAiSearchEnabled ? (
            <LocationFilterModalTabs />
          ) : (
            <MobileAutoComplete handleSelection={handleSelection} initialLocation={initialLocation} locationFilterProps={props} />
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
